import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import {
  Bell, Mail, Monitor, Save, Loader2,
  BellRing, Shield, FileText, Users, Clock, Ticket, BarChart3,
  Volume2, VolumeX, Play, GraduationCap, Lock, Eye, EyeOff, Check,
} from 'lucide-react';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import AgentSmsPreference from '@/components/notifications/AgentSmsPreference';
import TimezonePreferenceCard from '@/components/settings/TimezonePreferenceCard';
import {
  getNotificationSoundEnabled, setNotificationSoundEnabled,
  getNotificationSoundVolume, setNotificationSoundVolume,
  playNotificationChime,
} from '@/lib/notificationSound';

const EVENT_ICONS = {
  contract_status: FileText,
  contract_approved: FileText,
  new_agent: Users,
  ticket_update: Ticket,
  compliance_alert: Shield,
  invitation_status: Mail,
  stale_contract: Clock,
  weekly_digest: BarChart3,
  knowledge_published: GraduationCap,
};

export default function NotificationPreferencesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Phase 27.38.5 — client-side sound preference (localStorage-backed).
  const [soundEnabled, setSoundEnabled] = useState(() => getNotificationSoundEnabled());
  const [soundVolume, setSoundVolume] = useState(() => Math.round(getNotificationSoundVolume() * 100));

  useEffect(() => {
    fetchPrefs();
  }, []);

  const fetchPrefs = async () => {
    try {
      const res = await api.get('/notification-preferences/detailed');
      setData(res.data);
    } catch {
      toast.error('Failed to load preferences');
    }
    setLoading(false);
  };

  const updateGlobal = (key, value) => {
    setData(prev => ({
      ...prev,
      global: { ...prev.global, [key]: value },
    }));
    setDirty(true);
  };

  const updateEvent = (eventKey, channel, value) => {
    setData(prev => ({
      ...prev,
      events: prev.events.map(e =>
        e.key === eventKey ? { ...e, [channel]: value } : e
      ),
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        global_prefs: data.global,
        events: data.events.map(e => ({
          key: e.key,
          in_app: e.in_app,
          email: e.email,
          push: e.push,
        })),
      };
      const res = await api.put('/notification-preferences/detailed', payload);
      setData(res.data);
      setDirty(false);
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Failed to save preferences');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-preferences-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Settings</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Notification Preferences</h1>
          <p className="text-sm text-slate-500 mt-1">Control how and when you receive notifications.</p>
        </div>
        <Button onClick={handleSave} disabled={saving || !dirty} data-testid="save-preferences-btn" className="gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </Button>
      </div>

      {/* Global Toggles */}
      <TimezonePreferenceCard />
      <Card data-testid="global-toggles">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Bell size={16} className="text-sky-500" />
            Global Channels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-500">Master toggles — disabling a channel here turns it off for all events.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <Monitor size={16} className="text-slate-600" />
                <Label className="text-sm font-medium">In-App</Label>
              </div>
              <Switch
                checked={data.global.in_app_enabled}
                onCheckedChange={(v) => updateGlobal('in_app_enabled', v)}
                data-testid="global-in-app-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-slate-600" />
                <Label className="text-sm font-medium">Email</Label>
              </div>
              <Switch
                checked={data.global.email_enabled}
                onCheckedChange={(v) => updateGlobal('email_enabled', v)}
                data-testid="global-email-toggle"
              />
            </div>
          </div>
          {/* Phase 27.37.F — Agent SMS master toggle w/ affirmative consent. */}
          <AgentSmsPreference />

          {/* Phase 27.38.5 — Notification sound (opt-in). */}
          <div
            className="flex flex-col gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50"
            data-testid="notification-sound-block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                {soundEnabled ? (
                  <Volume2 size={16} className="text-violet-600 mt-0.5" />
                ) : (
                  <VolumeX size={16} className="text-slate-500 mt-0.5" />
                )}
                <div>
                  <Label className="text-sm font-medium">Play a sound for new-lead notifications</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    A soft chime plays in this browser when a new Lead
                    arrives (from Landing Pages or lead assignments).
                    Preference is saved on this device only.
                  </p>
                </div>
              </div>
              <Switch
                checked={soundEnabled}
                onCheckedChange={(v) => {
                  setSoundEnabled(!!v);
                  setNotificationSoundEnabled(!!v);
                  if (v) {
                    // Play immediately so the user hears the sound —
                    // this also acts as the user-gesture that unlocks
                    // AudioContext on browsers with autoplay policies.
                    playNotificationChime({ force: true });
                  }
                }}
                data-testid="notif-sound-toggle"
              />
            </div>
            {soundEnabled && (
              <div className="flex items-center gap-3 pl-6">
                <span className="text-xs text-slate-500 shrink-0">Volume</span>
                <Slider
                  value={[soundVolume]}
                  onValueChange={([v]) => {
                    setSoundVolume(v);
                    setNotificationSoundVolume(v / 100);
                  }}
                  min={5}
                  max={100}
                  step={5}
                  className="max-w-xs"
                  data-testid="notif-sound-volume"
                />
                <span className="text-xs text-slate-600 font-mono w-8">{soundVolume}%</span>
                <Button
                  size="sm" variant="outline"
                  onClick={() => playNotificationChime({ force: true })}
                  className="ml-auto h-7 text-xs"
                  data-testid="notif-sound-test"
                >
                  <Play size={11} className="mr-1" /> Test
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Per-Event Preferences */}
      <Card data-testid="event-preferences">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <BellRing size={16} className="text-violet-500" />
            Event Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-4">Choose which channels each event type uses. Disabled global channels are grayed out.</p>

          {/* Column headers */}
          <div className="hidden sm:grid grid-cols-[1fr_60px_60px_60px] gap-2 mb-3 px-3">
            <span className="text-[10px] uppercase text-slate-400 tracking-wider">Event</span>
            <span className="text-[10px] uppercase text-slate-400 tracking-wider text-center">In-App</span>
            <span className="text-[10px] uppercase text-slate-400 tracking-wider text-center">Email</span>
            <span className="text-[10px] uppercase text-slate-400 tracking-wider text-center">Push</span>
          </div>

          <div className="space-y-1">
            {data.events.map((evt) => {
              const Icon = EVENT_ICONS[evt.key] || Bell;
              return (
                <div key={evt.key} className="grid grid-cols-1 sm:grid-cols-[1fr_60px_60px_60px] gap-2 items-center p-3 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200">
                  <div className="flex items-center gap-3">
                    <Icon size={16} className="text-slate-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{evt.label}</p>
                      <p className="text-[11px] text-slate-400">{evt.description}</p>
                    </div>
                  </div>

                  {/* Mobile labels for small screens */}
                  <div className="flex sm:hidden gap-4 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={evt.in_app && data.global.in_app_enabled}
                        onCheckedChange={(v) => updateEvent(evt.key, 'in_app', v)}
                        disabled={!data.global.in_app_enabled}
                        data-testid={`event-${evt.key}-in-app`}
                      />
                      <span className="text-[10px] text-slate-400">In-App</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={evt.email && data.global.email_enabled}
                        onCheckedChange={(v) => updateEvent(evt.key, 'email', v)}
                        disabled={!data.global.email_enabled}
                        data-testid={`event-${evt.key}-email`}
                      />
                      <span className="text-[10px] text-slate-400">Email</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={evt.push}
                        onCheckedChange={(v) => updateEvent(evt.key, 'push', v)}
                        data-testid={`event-${evt.key}-push`}
                      />
                      <span className="text-[10px] text-slate-400">Push</span>
                    </div>
                  </div>

                  {/* Desktop toggles */}
                  <div className="hidden sm:flex justify-center">
                    <Switch
                      checked={evt.in_app && data.global.in_app_enabled}
                      onCheckedChange={(v) => updateEvent(evt.key, 'in_app', v)}
                      disabled={!data.global.in_app_enabled}
                      data-testid={`event-${evt.key}-in-app`}
                    />
                  </div>
                  <div className="hidden sm:flex justify-center">
                    <Switch
                      checked={evt.email && data.global.email_enabled}
                      onCheckedChange={(v) => updateEvent(evt.key, 'email', v)}
                      disabled={!data.global.email_enabled}
                      data-testid={`event-${evt.key}-email`}
                    />
                  </div>
                  <div className="hidden sm:flex justify-center">
                    <Switch
                      checked={evt.push}
                      onCheckedChange={(v) => updateEvent(evt.key, 'push', v)}
                      data-testid={`event-${evt.key}-push`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

/**
 * ChangePasswordCard — self-service credential rotation.
 *
 * Posts to `POST /api/auth/change-password` which verifies the current
 * password server-side (bcrypt), enforces new ≠ current, and updates
 * `users.password_hash` + `password_changed_at`. Session cookies stay
 * valid so the user doesn't get bounced out mid-flow.
 *
 * Client-side rules (mirrored on the server for defence-in-depth):
 *   • min 8 chars
 *   • new password must not equal current
 *   • confirmation must match new
 * These render as a live requirement checklist so the user knows what
 * they still need to type before the button unlocks.
 */
function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const rules = [
    { label: 'At least 8 characters', ok: next.length >= 8 },
    { label: 'Different from current password', ok: next.length > 0 && next !== current },
    { label: 'New passwords match', ok: next.length > 0 && next === confirm },
  ];
  const allOk = rules.every((r) => r.ok) && current.length > 0;

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); };

  const submit = async () => {
    if (!allOk) return;
    setSaving(true);
    try {
      await authAPI.changePassword(current, next);
      toast.success('Password updated');
      reset();
    } catch (err) {
      // Server surfaces plain strings for these errors — "Current
      // password is incorrect" / "New password must be different from
      // current". Fall back to the axios message if the payload is a
      // FastAPI validation array (shouldn't happen but guarded).
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((e) => e?.msg).filter(Boolean).join(' · ') : err?.message);
      toast.error(msg || 'Could not update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock size={18} className="text-slate-600" />
          Change Password
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Rotate your login password. You&apos;ll stay signed in on this device after the change.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm">Current password</Label>
          <div className="relative">
            <Input
              type={showCurrent ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your current password"
              data-testid="change-pwd-current"
              className="pr-9"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
              data-testid="change-pwd-toggle-current"
            >
              {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm">New password</Label>
            <div className="relative">
              <Input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                data-testid="change-pwd-new"
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label={showNext ? 'Hide new password' : 'Show new password'}
                data-testid="change-pwd-toggle-new"
              >
                {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Confirm new password</Label>
            <Input
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Retype your new password"
              data-testid="change-pwd-confirm"
            />
          </div>
        </div>

        <ul className="text-xs space-y-1 mt-1" data-testid="change-pwd-rules">
          {rules.map((r) => (
            <li key={r.label} className={`flex items-center gap-1.5 ${r.ok ? 'text-emerald-600' : 'text-slate-500'}`}>
              {r.ok
                ? <Check size={12} className="flex-shrink-0" />
                : <span className="inline-block w-3 h-3 rounded-full border border-slate-300 flex-shrink-0" />}
              {r.label}
            </li>
          ))}
        </ul>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={reset}
            disabled={saving || (!current && !next && !confirm)}
            data-testid="change-pwd-cancel"
          >
            Clear
          </Button>
          <Button
            onClick={submit}
            disabled={!allOk || saving}
            className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
            data-testid="change-pwd-submit"
          >
            {saving
              ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Updating…</>
              : <><Save size={14} className="mr-1.5" /> Update password</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
